import { Button, Modal, ModalFooter } from "@/components/ui";
import { PresenceImpactStayList } from "@/components/device/PresenceImpactStayList";
import type {
  DeviceStructureLeaveImpact,
  RoomPresenceAction,
} from "@/types";

type RoomChangeConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (action: RoomPresenceAction) => void;
  title: string;
  intro: string;
  newRoomLabel: string;
  newRoomName: string;
  currentHeading: string;
  futureHeading: string;
  transferHelp: string;
  discardHelp: string;
  transferText: string;
  discardText: string;
  cancelText: string;
  closeLabel: string;
  impact: DeviceStructureLeaveImpact;
  isConfirming?: boolean;
};

export function RoomChangeConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  intro,
  newRoomLabel,
  newRoomName,
  currentHeading,
  futureHeading,
  transferHelp,
  discardHelp,
  transferText,
  discardText,
  cancelText,
  closeLabel,
  impact,
  isConfirming = false,
}: RoomChangeConfirmModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeLabel={closeLabel}
      title={title}
      size="md"
      priority="high"
      role="alertdialog"
      showCloseButton={!isConfirming}
      closeOnEscape={!isConfirming}
      dismissOnBackdrop={!isConfirming}
      footer={
        <ModalFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isConfirming}
            className="w-full sm:w-auto"
          >
            {cancelText}
          </Button>
          <Button
            variant="outline"
            onClick={() => onConfirm("transfer")}
            isLoading={isConfirming}
            className="w-full sm:w-auto"
          >
            {transferText}
          </Button>
          <Button
            variant="error"
            onClick={() => onConfirm("discard")}
            isLoading={isConfirming}
            className="w-full sm:w-auto"
          >
            {discardText}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        <p className="text-[14px] text-surface-700">{intro}</p>
        <p className="text-[13px] text-surface-800">
          <span className="text-surface-500">{newRoomLabel}</span>{" "}
          <span className="font-semibold">{newRoomName}</span>
        </p>
        {impact.current.length > 0 ? (
          <div>
            <h4 className="text-[13px] font-semibold text-surface-900">
              {currentHeading}
            </h4>
            <PresenceImpactStayList items={impact.current} className="mt-2 space-y-2" />
          </div>
        ) : null}
        {impact.future.length > 0 ? (
          <div>
            <h4 className="text-[13px] font-semibold text-surface-900">
              {futureHeading}
            </h4>
            <PresenceImpactStayList items={impact.future} className="mt-2 space-y-2" />
          </div>
        ) : null}
        <div className="space-y-1 text-[12px] text-surface-500">
          <p>{transferHelp}</p>
          <p>{discardHelp}</p>
        </div>
      </div>
    </Modal>
  );
}
